import { useCallback, useEffect, useRef, useState } from 'react';
import { API_DOMAIN } from '@/api/trpc';
import { buildHeadersWithCsrfToken } from '@/api/csrf';

export type AgentSseEvent =
    | {
          type: 'status';
          status: 'queued' | 'running' | 'waiting_confirmation' | 'stalled' | 'succeeded' | 'failed' | 'canceled';
          sessionId?: number;
          runId?: number;
      }
    | {
          type: 'message';
          message: {
              id: number;
              sessionId: number;
              runId: number | null;
              role: 'user' | 'assistant' | 'tool_call' | 'tool_result' | 'system';
              ordinal: number;
              content: string | null;
              toolCallId: string | null;
              toolName: string | null;
              toolInputJson: string | null;
              toolResultJson: string | null;
              requiresConfirmation: boolean;
              confirmationStatus: 'pending' | 'approved' | 'rejected' | null;
          };
      }
    | { type: 'token'; token: string }
    | { type: 'tool_call'; toolCallId: string; toolName: string; toolInputJson: string }
    | { type: 'tool_result'; toolCallId: string; toolName: string; toolResultJson: string }
    | { type: 'confirmation_request'; toolCallId: string; toolName: string; toolInputJson: string; actionDescription: string }
    | {
          type: 'done';
          finishReason: string;
          sessionId: number;
          runId: number;
          inputTokens: number | null;
          outputTokens: number | null;
          totalCostNanoUsd?: number | null;
      }
    | { type: 'error'; message: string };

const VALID_SSE_TYPES = new Set(['status', 'message', 'token', 'tool_call', 'tool_result', 'confirmation_request', 'done', 'error']);

function isAgentSseEvent(data: unknown): data is AgentSseEvent {
    if (typeof data !== 'object' || data === null || !('type' in data)) return false;
    const { type } = data;
    return typeof type === 'string' && VALID_SSE_TYPES.has(type);
}

export type AgentRunStatus = Extract<AgentSseEvent, { type: 'status' }>['status'];

export type AgentExecutionMode = 'inline' | 'queued';

export type StreamAgentMessageParams = {
    sessionId?: number | null | undefined;
    message: string;
    pageContextJson: string | null;
    executionMode?: AgentExecutionMode;
    onEvent?: (event: AgentSseEvent) => void;
};

export type ConfirmToolCallParams = {
    toolCallId: string;
    approved: boolean;
    executionMode?: AgentExecutionMode;
    onEvent?: (event: AgentSseEvent) => void;
};

export function useAgentStream() {
    const abortControllerRef = useRef<AbortController | null>(null);
    const [isStreaming, setIsStreaming] = useState(false);
    const [status, setStatus] = useState<AgentRunStatus | null>(null);
    const [error, setError] = useState<string | null>(null);

    const abort = useCallback(() => {
        abortControllerRef.current?.abort();
    }, []);

    // Abort any in-flight stream when the component unmounts
    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    const streamSsePost = useCallback(
        async (params: {
            url: string;
            body: Record<string, unknown>;
            onEvent?: ((event: AgentSseEvent) => void) | undefined;
        }): Promise<Extract<AgentSseEvent, { type: 'done' }> | null> => {
            abortControllerRef.current?.abort();
            const abortController = new AbortController();
            abortControllerRef.current = abortController;

            setIsStreaming(true);
            setStatus(null);
            setError(null);

            let doneEvent: Extract<AgentSseEvent, { type: 'done' }> | null = null;
            const decoder = new TextDecoder();
            let buffer = '';

            try {
                const requestBody = JSON.stringify(params.body);

                const doRequest = async (options?: { forceRefreshCsrf?: boolean }): Promise<Response> => {
                    const headers = await buildHeadersWithCsrfToken({
                        apiDomain: API_DOMAIN,
                        headers: { 'Content-Type': 'application/json' },
                        forceRefresh: options?.forceRefreshCsrf
                    });

                    return fetch(params.url, {
                        method: 'POST',
                        headers,
                        credentials: 'include',
                        signal: abortController.signal,
                        body: requestBody
                    });
                };

                const parseErrorMessages = (raw: string): string[] | null => {
                    try {
                        const parsed = JSON.parse(raw) as { errorMessages?: unknown };
                        if (!Array.isArray(parsed.errorMessages)) return null;
                        const list = parsed.errorMessages.filter((m): m is string => typeof m === 'string' && m.trim().length > 0);
                        return list.length ? list : null;
                    } catch {
                        return null;
                    }
                };

                let response: Response | null = null;
                let lastError: { status: number; errorMessages: string[] | null } | null = null;

                for (let attempt = 0; attempt < 2; attempt++) {
                    const forceRefresh = attempt === 1;
                    const res = await doRequest({ forceRefreshCsrf: forceRefresh });

                    if (res.ok) {
                        response = res;
                        break;
                    }

                    const raw = await res.text();
                    const errorMessages = parseErrorMessages(raw);
                    lastError = { status: res.status, errorMessages };

                    const shouldRetryCsrf =
                        !forceRefresh && res.status === 403 && (errorMessages?.[0] ?? null) === 'CSRF validation failed';

                    if (shouldRetryCsrf) {
                        continue;
                    }

                    break;
                }

                if (!response) {
                    const statusCode = lastError?.status ?? 0;
                    const details = lastError?.errorMessages?.length ? lastError.errorMessages.join(', ') : null;

                    const message = details
                        ? `Agent stream failed (HTTP ${statusCode}): ${details}`
                        : `Agent stream failed (HTTP ${statusCode})`;

                    setError(message);
                    throw new Error(message);
                }

                const reader = response.body?.getReader();

                if (!reader) {
                    const message = 'Agent stream unavailable (no response body)';
                    setError(message);
                    throw new Error(message);
                }

                const handleEvent = (event: AgentSseEvent) => {
                    params.onEvent?.(event);

                    if (event.type === 'status') {
                        setStatus(event.status);
                        return;
                    }

                    if (event.type === 'error') {
                        setError(event.message);
                        return;
                    }

                    if (event.type === 'done') {
                        doneEvent = event;
                    }
                };

                let shouldContinue = true;

                while (shouldContinue) {
                    const { value, done } = await reader.read();

                    if (done) {
                        shouldContinue = false;
                        break;
                    }

                    buffer += decoder.decode(value, { stream: true });

                    const parts = buffer.split('\n\n');
                    buffer = parts.pop() ?? '';

                    for (const part of parts) {
                        const lines = part.split('\n');

                        for (const line of lines) {
                            const trimmed = line.trim();
                            if (!trimmed.startsWith('data:')) continue;

                            const jsonRaw = trimmed.slice('data:'.length).trim();
                            if (!jsonRaw) continue;

                            try {
                                const parsed: unknown = JSON.parse(jsonRaw);

                                if (isAgentSseEvent(parsed)) {
                                    handleEvent(parsed);
                                }
                            } catch {
                                handleEvent({ type: 'error', message: 'Malformed agent stream event' });
                            }
                        }
                    }
                }
            } finally {
                setIsStreaming(false);
                abortControllerRef.current = null;
            }

            return doneEvent;
        },
        []
    );

    const streamMessage = useCallback(
        (params: StreamAgentMessageParams) =>
            streamSsePost({
                url: `${API_DOMAIN}/api/agent/stream`,
                body: {
                    sessionId: params.sessionId ?? null,
                    message: params.message,
                    pageContextJson: params.pageContextJson,
                    executionMode: params.executionMode
                },
                onEvent: params.onEvent
            }),
        [streamSsePost]
    );

    const confirmToolCall = useCallback(
        (params: ConfirmToolCallParams) =>
            streamSsePost({
                url: `${API_DOMAIN}/api/agent/confirm/${encodeURIComponent(params.toolCallId)}`,
                body: { approved: params.approved, executionMode: params.executionMode },
                onEvent: params.onEvent
            }),
        [streamSsePost]
    );

    const cancelRun = useCallback(async (params: { runId: number }) => {
        if (!Number.isFinite(params.runId) || params.runId <= 0) throw new Error('Invalid runId');

        const headers = await buildHeadersWithCsrfToken({
            apiDomain: API_DOMAIN,
            headers: { 'Content-Type': 'application/json' }
        });

        const response = await fetch(`${API_DOMAIN}/api/agent/cancel/${encodeURIComponent(String(params.runId))}`, {
            method: 'POST',
            headers,
            credentials: 'include'
        });

        if (!response.ok) {
            const message = `Failed to cancel run (HTTP ${response.status})`;
            setError(message);
            throw new Error(message);
        }
    }, []);

    return { streamMessage, confirmToolCall, cancelRun, abort, isStreaming, status, error };
}
